import Request from 'core/api/Request';
import urlResolver from 'edrolo/utils/URLResolver';

const responseAndErrorResolver = (resolve) => (err, res) => {
  if (err) {
    // err will be an Error object, with .status (eg 403)
    // and .message (eg "Forbidden") properties.
    // It will also have .response, which is the same response object as `res`
    // TODO: Should we be using err.response.body here instead? - Matt
    resolve({
      status: err.status,
      message: err.message,
      errors: res ? res.body : err,
    });
  } else {
    resolve({ response: res.body, statusCode: res.statusCode });
  }
};

export function fetch(url) {
  return new Promise((resolve) => {
    Request.get(url).end(responseAndErrorResolver(resolve));
  });
}

export function post(url, data) {
  return new Promise((resolve) => {
    return Request.post(url)
      .send(data.toJS ? data.toJS() : data)
      .end(responseAndErrorResolver(resolve));
  });
}

export function del(url) {
  return new Promise((resolve) => {
    Request.delete(url).end(responseAndErrorResolver(resolve));
  });
}

export function put(url, data) {
  return new Promise((resolve) => {
    Request.put(url)
      .send(data.toJS ? data.toJS() : data)
      .end(responseAndErrorResolver(resolve));
  });
}

export function loadCourseProgress(userId, classId) {
  const url = urlResolver.reverse('api:user-progress-course', {
    userId,
    classId,
  });
  return fetch(url);
}

export function loadCourseContent(classId) {
  const url = urlResolver.reverse('api:course_summary_by_class_id', {
    classId,
  });
  return fetch(url);
}

export function loadLesson(lessonId) {
  const url = urlResolver.reverse('api:application-lesson-detail', {
    lessonId,
  });
  return fetch(url);
}

export function loadClassDetail(classId) {
  const detailUrl = urlResolver.reverse('api:class-detail', { classId });
  return fetch(detailUrl);
}

export function loadPrivilegedContent(classId) {
  const privilegedContentUrl = urlResolver.reverse('api:privileged-content', {
    classId,
  });
  return fetch(privilegedContentUrl);
}

export function loadClassProgress(classId) {
  const url = urlResolver.reverse('api:class-course-progress', { classId });
  return fetch(url);
}

export function loadClassLessonProgress(classId, lessonId) {
  const url = urlResolver.reverse('api:class-lesson-progress', {
    classId,
    lessonId,
  });
  return fetch(url);
}

export function deleteExamSitting(classId, examId, sittingId) {
  const url = urlResolver.reverse('api:class-exam-sitting', {
    classId,
    examId,
    sittingId,
  });
  return del(url);
}

export const teacherNotes = {
  create: (lessonId, note) => {
    const url = urlResolver.reverse('api:class-teacher-notes-list', {
      lessonId,
    });
    return post(url, note);
  },
  update: (lessonId, note) => {
    const url = urlResolver.reverse('api:class-teacher-notes-detail', {
      lessonId,
      noteId: note.id,
    });
    return put(url, note);
  },
  delete: (lessonId, noteId) => {
    const url = urlResolver.reverse('api:class-teacher-notes-detail', {
      lessonId,
      noteId,
    });
    return del(url);
  },
};

export const attachments = {
  create: (contentType, objectId, attachment) => {
    const { files, ...newAttachment } = attachment;
    newAttachment.content_type = contentType;
    newAttachment.object_id = objectId;

    const url = urlResolver.reverse('api:attachment-list');
    return new Promise((resolve) => {
      const req = Request.post(url);

      if (files !== undefined) {
        files.forEach((file) => req.attach('attachment_file', file, file.name));
      }

      for (const fieldName in newAttachment) {
        if (Object.hasOwnProperty.call(newAttachment, fieldName)) {
          req.field(fieldName, newAttachment[fieldName]);
        }
      }
      req.end(responseAndErrorResolver(resolve));
    });
  },
  update: (attachment) => {
    const url = urlResolver.reverse('api:attachment-detail', {
      attachmentId: attachment.id,
    });
    return put(url, attachment);
  },
  delete: (attachment) => {
    const url = urlResolver.reverse('api:attachment-detail', {
      attachmentId: attachment.id,
    });
    return del(url);
  },
};

export const lockClassResource = (classId, resourceId, showToStudents) => {
  const data = {
    show_to_students: showToStudents,
  };
  const url = urlResolver.reverse('api:post:class-resource-lock', {
    classId,
    resourceId,
  });
  return post(url, data);
};

export const lockExam = (classId, examId, isLocked) => {
  const data = {
    is_locked: isLocked,
  };
  const url = urlResolver.reverse('api:class-exam-lock', {
    classId,
    examId,
  });
  return post(url, data);
};

export const setExamDueDate = (classId, examId, dueDate) => {
  const data = {
    due_date: dueDate,
  };
  const url = urlResolver.reverse('api:class-exam-lock', {
    classId,
    examId,
  });
  return post(url, data);
};
